import React, { useState } from 'react';
import { CustomerInfo, CartItem } from '../../types';
import Input from '../atoms/Input';
import Button from '../atoms/Button';
import './CustomerInfoForm.css';

interface CustomerInfoFormProps {
  initialData?: Partial<CustomerInfo>;
  cartItems?: CartItem[];
  onSubmit: (customerInfo: CustomerInfo) => void;
  onInfoChange?: (customerInfo: Partial<CustomerInfo>) => void;
  onCancel?: () => void;
  loading?: boolean;
  className?: string;
}

export const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({
  initialData,
  cartItems = [],
  onSubmit,
  onInfoChange,
  onCancel,
  loading = false,
  className = ''
}) => {
  // Determine delivery option from cart items
  const getDeliveryOptionFromCart = (): 'collection' | 'delivery' => {
    if (cartItems.length === 0) return 'collection';
    
    // Check if any item has delivery option
    const hasDelivery = cartItems.some(item => item.deliveryOption === 'delivery');
    return hasDelivery ? 'delivery' : 'collection';
  };

  const [formData, setFormData] = useState<Partial<CustomerInfo>>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Ireland'
    },
    deliveryInstructions: '',
    deliveryOption: getDeliveryOptionFromCart(),
    ...initialData
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    console.log('📝 CustomerInfoForm.handleInputChange called:', { field, value });

    setFormData(prev => {
      let newData;
      if (field.startsWith('address.')) {
        const addressField = field.split('.')[1];
        newData = {
          ...prev,
          address: {
            ...prev.address,
            [addressField]: value
          } as CustomerInfo['address']
        };
        console.log('🏠 Address field updated:', addressField, '=', value);
      } else {
        newData = {
          ...prev,
          [field]: value
        };
        console.log('📝 Regular field updated:', field, '=', value);
      }

      // Call onInfoChange for shipping-related fields
      if (onInfoChange && (field === 'deliveryOption' || field.startsWith('address.'))) {
        console.log('🔄 Calling onInfoChange with new data:', newData);
        onInfoChange(newData);
      } else {
        console.log('⏭️ Skipping onInfoChange - field not shipping-related or callback not provided');
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    // Address validation - only required for delivery
    if (formData.deliveryOption === 'delivery') {
      if (!formData.address?.line1?.trim()) {
        newErrors['address.line1'] = 'Address line 1 is required for delivery';
      }

      if (!formData.address?.city?.trim()) {
        newErrors['address.city'] = 'City is required for delivery';
      }

      if (!formData.address?.postalCode?.trim()) {
        newErrors['address.postalCode'] = 'Postal code is required for delivery';
      }

      if (!formData.address?.country?.trim()) {
        newErrors['address.country'] = 'Country is required for delivery';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData as CustomerInfo);
    }
  };

  return (
    <div className={`customer-info-form ${className}`}>
      <div className="customer-info-form__header">
        <h3 className="customer-info-form__title">Customer Information</h3>
        <p className="customer-info-form__description">
          Please provide your contact and delivery information.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="customer-info-form__form">
        <div className="customer-info-form__section">
          <h4 className="customer-info-form__section-title">Contact Details</h4>
          
          <div className="customer-info-form__field">
            <label className="customer-info-form__label">
              Email Address *
            </label>
            <Input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className={`customer-info-form__input ${errors.email ? 'customer-info-form__input--error' : ''}`}
            />
            {errors.email && (
              <span className="customer-info-form__error">{errors.email}</span>
            )}
          </div>

          <div className="customer-info-form__row">
            <div className="customer-info-form__field">
              <label className="customer-info-form__label">
                First Name *
              </label>
              <Input
                type="text"
                value={formData.firstName || ''}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="John"
                className={`customer-info-form__input ${errors.firstName ? 'customer-info-form__input--error' : ''}`}
              />
              {errors.firstName && (
                <span className="customer-info-form__error">{errors.firstName}</span>
              )}
            </div>

            <div className="customer-info-form__field">
              <label className="customer-info-form__label">
                Last Name *
              </label>
              <Input
                type="text"
                value={formData.lastName || ''}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Doe"
                className={`customer-info-form__input ${errors.lastName ? 'customer-info-form__input--error' : ''}`}
              />
              {errors.lastName && (
                <span className="customer-info-form__error">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="customer-info-form__field">
            <label className="customer-info-form__label">
              Phone Number *
            </label>
            <Input
              type="text"
              value={formData.phone || ''}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+353 1 234 5678"
              className={`customer-info-form__input ${errors.phone ? 'customer-info-form__input--error' : ''}`}
            />
            {errors.phone && (
              <span className="customer-info-form__error">{errors.phone}</span>
            )}
          </div>
        </div>

        <div className="customer-info-form__section">
          <h4 className="customer-info-form__section-title">Delivery Option</h4>
          <div className="customer-info-form__delivery-info">
            <div className={`customer-info-form__delivery-option-display ${formData.deliveryOption === 'delivery' ? 'delivery' : 'collection'}`}>
              <div className="customer-info-form__delivery-icon">
                {formData.deliveryOption === 'delivery' ? '🚚' : '🏪'}
              </div>
              <div className="customer-info-form__delivery-details">
                <strong>
                  {formData.deliveryOption === 'delivery' ? 'Delivery' : 'Collection'}
                </strong>
                <small>
                  {formData.deliveryOption === 'delivery' 
                    ? 'We\'ll deliver to your address' 
                    : 'Pick up from our store'
                  }
                </small>
              </div>
            </div>
            {formData.deliveryOption === 'delivery' && (
              <div className="customer-info-form__delivery-note">
                <small>💡 Delivery option selected from your cart items</small>
              </div>
            )}
          </div>
        </div>

        {formData.deliveryOption === 'delivery' && (
          <div className="customer-info-form__section">
            <h4 className="customer-info-form__section-title">Delivery Address</h4>
          
          <div className="customer-info-form__field">
            <label className="customer-info-form__label">
              Address Line 1 *
            </label>
            <Input
              type="text"
              value={formData.address?.line1 || ''}
              onChange={(e) => handleInputChange('address.line1', e.target.value)}
              placeholder="123 Main Street"
              className={`customer-info-form__input ${errors['address.line1'] ? 'customer-info-form__input--error' : ''}`}
            />
            {errors['address.line1'] && (
              <span className="customer-info-form__error">{errors['address.line1']}</span>
            )}
          </div>

          <div className="customer-info-form__field">
            <label className="customer-info-form__label">
              Address Line 2
            </label>
            <Input
              type="text"
              value={formData.address?.line2 || ''}
              onChange={(e) => handleInputChange('address.line2', e.target.value)}
              placeholder="Apartment, suite, etc. (optional)"
              className="customer-info-form__input"
            />
          </div>

          <div className="customer-info-form__row">
            <div className="customer-info-form__field">
              <label className="customer-info-form__label">
                City *
              </label>
              <Input
                type="text"
                value={formData.address?.city || ''}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                placeholder="Dublin"
                className={`customer-info-form__input ${errors['address.city'] ? 'customer-info-form__input--error' : ''}`}
              />
              {errors['address.city'] && (
                <span className="customer-info-form__error">{errors['address.city']}</span>
              )}
            </div>

            <div className="customer-info-form__field">
              <label className="customer-info-form__label">
                State/County
              </label>
              <Input
                type="text"
                value={formData.address?.state || ''}
                onChange={(e) => handleInputChange('address.state', e.target.value)}
                placeholder="Dublin"
                className="customer-info-form__input"
              />
            </div>
          </div>

          <div className="customer-info-form__row">
            <div className="customer-info-form__field">
              <label className="customer-info-form__label">
                Postal Code *
              </label>
              <Input
                type="text"
                value={formData.address?.postalCode || ''}
                onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                placeholder="D01 AB23"
                className={`customer-info-form__input ${errors['address.postalCode'] ? 'customer-info-form__input--error' : ''}`}
              />
              {errors['address.postalCode'] && (
                <span className="customer-info-form__error">{errors['address.postalCode']}</span>
              )}
            </div>

            <div className="customer-info-form__field">
              <label className="customer-info-form__label">
                Country *
              </label>
              <select
                value={formData.address?.country || 'Ireland'}
                onChange={(e) => handleInputChange('address.country', e.target.value)}
                className={`customer-info-form__select ${errors['address.country'] ? 'customer-info-form__select--error' : ''}`}
              >
                <option value="Ireland">Ireland</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
                <option value="Other">Other</option>
              </select>
              {errors['address.country'] && (
                <span className="customer-info-form__error">{errors['address.country']}</span>
              )}
            </div>
          </div>
          </div>
        )}

        {formData.deliveryOption === 'delivery' && (
          <div className="customer-info-form__section">
            <h4 className="customer-info-form__section-title">Delivery Instructions</h4>
          
          <div className="customer-info-form__field">
            <label className="customer-info-form__label">
              Message on cake
            </label>
            <Input
              type="textarea"
              value={formData.deliveryInstructions || ''}
              onChange={(e) => handleInputChange('deliveryInstructions', e.target.value)}
              placeholder="Any special delivery instructions, access codes, or notes for the delivery person (optional)"
              className="customer-info-form__input"
            />
          </div>
          </div>
        )}

        <div className="customer-info-form__actions">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={loading}
              className="customer-info-form__cancel-button"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="customer-info-form__submit-button"
          >
            {loading ? 'Processing...' : 'Continue to Payment'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomerInfoForm;
